import React from "react";

type RetailVisibilityStatus = "not_run" | "ready";
type RetailSignalStatus = "Strong" | "Moderate" | "Weak" | "Absent";

type RetailVisibilityProps = {
  status?: RetailVisibilityStatus;
  score?: number;

  headline?: string;
  narrative?: string;
  recommendedAction?: string;

  customerBrandMentionRate?: number;
  priorityRetailerMentionRate?: number;
  ownedChannelMentionRate?: number;

  priorityChannels?: Array<{
  name: string;
  role: string;
  mentions: number;
  visibilityRate?: number;
  interpretation: RetailSignalStatus;
}>;

channelAlternatives?: Array<{
  name: string;
  mentions: number;
  visibilityRate?: number;
}>;

topCompetitors?: Array<{
  name: string;
  mentions: number;
  visibilityRate?: number;
}>;

  productInsights?: Array<{
    label: string;
    status: "Strong" | "Moderate" | "Weak";
    detail: string;
  }>;
};

export function AIRetailVisibility({
  status = "not_run",
  score = 0,
  headline = "Retail visibility has not been analyzed yet.",
  narrative = "This account is configured for AI Retail Visibility, but no retail prompt results are available for the selected run.",
  recommendedAction = "Run a retail visibility analysis to identify channel, retailer, and product-level opportunities.",
  customerBrandMentionRate = 0,
  priorityRetailerMentionRate = 0,
  ownedChannelMentionRate = 0,
  priorityChannels = [],
  channelAlternatives = [],
  topCompetitors = [],
  productInsights = [],
}: RetailVisibilityProps) {
  return (
    <section style={styles.card}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Toren Retail</p>
          <div style={styles.titleRow}>
             <h2 style={styles.title}>AI Retail Visibility</h2>
             <span style={styles.betaBadge}>Beta Feature</span>
          </div>
          <p style={styles.subtitle}>
          AI retail visibility shows whether AI-assisted shoppers are being routed toward the customer’s preferred retail paths or toward competing brands, marketplaces, and specialty sellers.
          </p>
        </div>

        {status === "ready" && (
          <div style={styles.statusPill}>
            <span style={styles.statusLabel}>Retail Status</span>
            <strong>{scoreLabel(score)}</strong>
          </div>
        )}
      </div>

      {status === "not_run" && (
        <div style={styles.emptyBox}>
          <strong>Retail visibility has not been analyzed yet.</strong>
          <p style={styles.emptyText}>
            This account is configured for AI Retail Visibility, but no retail
            prompt results are available for the selected run.
          </p>
        </div>
      )}

      {status === "ready" && (
        <>
          <div style={styles.insightBox}>
            <p style={styles.insightLabel}>Executive Readout</p>
            <h3 style={styles.insightTitle}>{headline}</h3>
            <p style={styles.insightText}>{narrative}</p>
          </div>

          <div style={styles.metricGrid}>
            <MetricCard
              label="AI Retail Position"
              value={scoreLabel(score)}
              detail={`Composite score: ${score}`}
            />
            <MetricCard
              label="Forney Brand Visibility"
              value={`${customerBrandMentionRate}%`}
              detail="Forney inclusion across retail shopping prompts"
            />
            <MetricCard
              label="Priority Retail Presence"
              value={`${priorityRetailerMentionRate}%`}
              detail="Existing retail paths surfaced by AI"
            />
            <MetricCard
              label="Owned Commerce"
              value={`${ownedChannelMentionRate}%`}
              detail="AI understanding of Forney led buying paths"
            />
          </div>

          <div style={styles.twoColumnGrid}>
            <Panel title="Current AI Channel">
              {priorityChannels.length ? (
                <div style={styles.list}>
                  {priorityChannels.map((channel) => (
                    <div key={channel.name} style={styles.channelRow}>
                      <div>
                        <strong style={styles.rowTitle}>{channel.name}</strong>
                        <p style={styles.rowSubtext}>{channel.role}</p>
                      </div>
                      <div style={styles.rowRight}>
                        <span style={styles.rowRightCompact}>
                        <strong>{channel.visibilityRate ?? 0}%</strong>
                        <span style={styles.rowSubtext}>{channel.mentions} mentions</span>
                        </span>
                        <StatusBadge status={channel.interpretation} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No priority channel data available yet." />
              )}
            </Panel>

            <Panel title="Organic Retailer Landscape">
              {channelAlternatives.length ? (
                <div style={styles.list}>
                  {channelAlternatives.map((channel) => (
                    <div key={channel.name} style={styles.simpleRow}>
                      <span>{channel.name}</span>
                      <span style={styles.rowRightCompact}>
                      <strong>{channel.visibilityRate ?? 0}%</strong>
                      <span style={styles.rowSubtext}>{channel.mentions} mentions</span>
                    </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No alternate channel data available yet." />
              )}
            </Panel>
          </div>

          <div style={styles.twoColumnGrid}>
            <Panel title="Product-Level Visibility">
              {productInsights.length ? (
                <div style={styles.list}>
                  {productInsights.map((insight) => (
                    <div key={insight.label} style={styles.productRow}>
                      <div style={styles.productHeader}>
                        <strong>{insight.label}</strong>
                        <StatusBadge status={insight.status} />
                      </div>
                      <p style={styles.productText}>{insight.detail}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No product visibility data available yet." />
              )}
            </Panel>

            <Panel title="AI Retail Competitor Landscape">
              {topCompetitors.length ? (
                <div style={styles.list}>
                  {topCompetitors.map((competitor) => (
                    <div key={competitor.name} style={styles.simpleRow}>
                      <span>{competitor.name}</span>
                      <span style={styles.rowRightCompact}>
                        <strong>{competitor.visibilityRate ?? 0}%</strong>
                        <span style={styles.rowSubtext}>{competitor.mentions} mentions</span>
                      </span>
                  </div>
                ))}
                </div>
              ) : (
                <EmptyState message="No competitor retail data available yet." />
              )}
            </Panel>
          </div>

          <div style={styles.actionBox}>
            <p style={styles.actionLabel}>What To Do Next</p>
            <p style={styles.actionText}>{recommendedAction}</p>
          </div>
        </>
      )}
    </section>
  );
}

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div style={styles.metricCard}>
      <p style={styles.metricLabel}>{label}</p>
      <div style={styles.metricValue}>{value}</div>
      <p style={styles.metricDetail}>{detail}</p>
    </div>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={styles.panel}>
      <h3 style={styles.panelTitle}>{title}</h3>
      {children}
    </div>
  );
}

function StatusBadge({ status }: { status: RetailSignalStatus }) {
  const style =
    status === "Strong"
      ? styles.badgeStrong
      : status === "Moderate"
      ? styles.badgeModerate
      : status === "Weak"
      ? styles.badgeWeak
      : styles.badgeAbsent;

  return <span style={{ ...styles.badge, ...style }}>{status}</span>;
}

function EmptyState({ message }: { message: string }) {
  return <div style={styles.emptyState}>{message}</div>;
}

function scoreLabel(score: number) {
  if (score >= 75) return "Strong";
  if (score >= 50) return "Moderate";
  if (score >= 25) return "Developing";
  return "Weak";
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    padding: 24,
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
    marginTop: 24,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    alignItems: "flex-start",
    marginBottom: 20,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "#0e7490",
    fontWeight: 800,
  },
  title: {
    margin: "4px 0 0",
    fontSize: 24,
    lineHeight: 1.2,
    color: "#0f172a",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
    maxWidth: 860,
  },
  statusPill: {
    minWidth: 140,
    textAlign: "right",
    background: "#0f172a",
    color: "#fff",
    padding: "12px 14px",
    borderRadius: 16,
  },
  statusLabel: {
    display: "block",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#cbd5e1",
    marginBottom: 3,
  },
  insightBox: {
    border: "1px solid #bae6fd",
    background: "#f0f9ff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
  },
  insightLabel: {
    margin: 0,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "#0369a1",
    fontWeight: 800,
  },
  insightTitle: {
    margin: "6px 0 0",
    color: "#0f172a",
    fontSize: 17,
    lineHeight: 1.35,
  },
  insightText: {
    margin: "8px 0 0",
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.65,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
  },
  metricCard: {
    border: "1px solid #e2e8f0",
    borderTop: "4px solid #3b82f6",
    borderRadius: 14,
    padding: 16,
    background: "#ffffff",
  },
  metricLabel: {
    margin: 0,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "#64748b",
    fontWeight: 800,
  },
  metricValue: {
    marginTop: 18,
    fontSize: 30,
    fontWeight: 900,
    color: "#0f172a",
    lineHeight: 1,
  },
  metricDetail: {
    margin: "8px 0 0",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.4,
  },
  rowRightCompact: {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 2,
  color: "#0f172a",
  fontSize: 13,
},
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
    marginTop: 16,
  },
  panel: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 16,
    background: "#ffffff",
  },
  panelTitle: {
    margin: "0 0 12px",
    fontSize: 15,
    color: "#0f172a",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  titleRow: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
},
betaBadge: {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "3px 9px",
  background: "#eef2ff",
  border: "1px solid #c7d2fe",
  color: "#4338ca",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
},
  channelRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    background: "#f8fafc",
    borderRadius: 10,
  },
  rowTitle: {
    color: "#0f172a",
    fontSize: 13,
  },
  rowSubtext: {
    margin: "3px 0 0",
    color: "#64748b",
    fontSize: 12,
  },
  rowRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
    minWidth: 88,
    color: "#0f172a",
    fontSize: 13,
  },
  simpleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "9px 12px",
    background: "#f8fafc",
    borderRadius: 10,
    color: "#334155",
    fontSize: 13,
  },
  productRow: {
    padding: "10px 12px",
    background: "#f8fafc",
    borderRadius: 10,
  },
  productHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: "#0f172a",
    fontSize: 13,
  },
  productText: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.5,
  },
  actionBox: {
    marginTop: 16,
    border: "1px solid #fde68a",
    background: "#fffbeb",
    borderRadius: 14,
    padding: 16,
  },
  actionLabel: {
    margin: 0,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "#92400e",
    fontWeight: 800,
  },
  actionText: {
    margin: "8px 0 0",
    color: "#78350f",
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 600,
  },
  emptyBox: {
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    borderRadius: 14,
    padding: 16,
    color: "#334155",
  },
  emptyText: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 13,
  },
  emptyState: {
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    borderRadius: 10,
    padding: 14,
    fontSize: 13,
    background: "#f8fafc",
  },
  badge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "2px 8px",
    border: "1px solid",
    fontSize: 11,
    fontWeight: 800,
  },
  badgeStrong: {
    background: "#ecfdf5",
    borderColor: "#a7f3d0",
    color: "#047857",
  },
  badgeModerate: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  },
  badgeWeak: {
    background: "#fffbeb",
    borderColor: "#fde68a",
    color: "#b45309",
  },
  badgeAbsent: {
    background: "#f8fafc",
    borderColor: "#cbd5e1",
    color: "#64748b",
  },
};